import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, X } from 'lucide-react';
import { API } from '../../App';

// ERPNext-style link autocomplete. Resolves to a doctype id; displays the
// title field of the linked record. Search-as-you-type against the linked
// doctype's search_fields.
export default function LinkField({ value, onChange, doctype, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [labelMap, setLabelMap] = useState({});
  const [meta, setMeta] = useState(null);
  const containerRef = useRef(null);

  // Load meta (for title_field) once.
  useEffect(() => {
    if (!doctype) return;
    axios.get(`${API}/doctype/${doctype}/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, [doctype]);

  // If we have a value but no label, fetch it.
  useEffect(() => {
    if (!value || !doctype || labelMap[value] || !meta) return;
    axios.get(`${API}/doctype/${doctype}/${value}`).then(r => {
      const title = r.data?.[meta.title_field] || value;
      setLabelMap(m => ({ ...m, [value]: title }));
    }).catch(() => setLabelMap(m => ({ ...m, [value]: value })));
  }, [value, doctype, meta]); // eslint-disable-line

  // Debounced search.
  const search = useCallback(async (text) => {
    if (!doctype) return;
    try {
      const r = await axios.get(`${API}/doctype/${doctype}/`, { params: { q: text || undefined, limit: 10 } });
      const items = r.data?.items || [];
      setResults(items);
      const newMap = { ...labelMap };
      items.forEach(it => { if (meta) newMap[it.id] = it[meta.title_field] || it.id; });
      setLabelMap(newMap);
    } catch { setResults([]); }
  }, [doctype, meta, labelMap]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(query), 200);
    return () => clearTimeout(t);
  }, [query, open, search]);

  // Outside-click close.
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const display = value ? (labelMap[value] || value) : '';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 8px 6px 10px', fontSize: 13,
          border: '1px solid var(--erp-border)', borderRadius: 5,
          background: disabled ? 'var(--erp-surface-2)' : 'var(--erp-surface)',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onClick={() => { if (!disabled) { setOpen(true); setTimeout(() => search(''), 0); } }}
      >
        <span style={{ flex: 1, color: display ? 'var(--erp-text)' : 'var(--erp-text-faint)' }}>
          {display || placeholder || `Pick ${doctype}…`}
        </span>
        {value && !disabled && (
          <button onClick={e => { e.stopPropagation(); onChange(''); }} style={{ background: 'none', border: 0, padding: 0, color: 'var(--erp-text-muted)', cursor: 'pointer' }}>
            <X size={12} />
          </button>
        )}
        <ChevronDown size={14} color="var(--erp-text-muted)" />
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: 'var(--erp-surface)', border: '1px solid var(--erp-border)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', maxHeight: 280, overflowY: 'auto' }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={`Search ${doctype}…`}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: 0, borderBottom: '1px solid var(--erp-border)', outline: 'none' }}
          />
          {results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--erp-text-muted)', textAlign: 'center' }}>No matches</div>
          ) : results.map(r => (
            <div
              key={r.id}
              onClick={() => { onChange(r.id); setOpen(false); setQuery(''); }}
              style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--erp-border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--erp-surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div>{meta && r[meta.title_field]}</div>
              <div style={{ fontSize: 11, color: 'var(--erp-text-faint)' }}>{r.id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
